export class TranscriptLoadGate {
    sessionId = null;
    state = 'ready';
    /** A server fetch for `sessionId` started: the session is read-only until it settles. */
    begin(sessionId) {
        this.sessionId = sessionId;
        this.state = 'loading';
        return this.view();
    }
    /** The fetch for `sessionId` returned its transcript (or the transcript was already held locally). */
    succeed(sessionId) {
        if (this.sessionId === sessionId)
            this.state = 'ready';
        return this.view();
    }
    /** The fetch for `sessionId` failed. The session stays read-only. */
    fail(sessionId) {
        if (this.sessionId === sessionId)
            this.state = 'failed';
        return this.view();
    }
    /** A new chat, a different user, or a session opened from a complete local copy. */
    reset() {
        this.sessionId = null;
        this.state = 'ready';
        return this.view();
    }
    /** True unless `sessionId` is the session whose transcript is loading or failed to load. */
    canWrite(sessionId) {
        return this.state === 'ready' || !sessionId || this.sessionId !== sessionId;
    }
    /** Why `sessionId` cannot be written to right now, or undefined when it can. */
    blockedState(sessionId) {
        return this.canWrite(sessionId) ? undefined : this.state;
    }
    /**
     * The sentence to show when a write to `sessionId` is refused, or undefined when it can be written. Every write path
     * (send, autosave, rename) asks this one question, so none of them can write around the gate.
     */
    refusalMessage(sessionId) {
        const blocked = this.blockedState(sessionId);
        return blocked === undefined ? undefined : blocked === 'loading' ? TRANSCRIPT_LOADING_MESSAGE : TRANSCRIPT_FAILED_MESSAGE;
    }
    view() {
        return { sessionId: this.sessionId, state: this.state };
    }
}
export const TRANSCRIPT_LOADING_MESSAGE = 'This conversation is still loading. Wait a moment, then send again.';
export const TRANSCRIPT_FAILED_MESSAGE = 'This conversation could not be loaded, so nothing can be added to it yet. Retry loading it, or start a new chat.';
/** What saving this transcript would send, as the autosave fingerprints it. */
export function transcriptFingerprint(id, title, turns) {
    return JSON.stringify([id, title, turns]);
}
/**
 * The turns a history save carries for `messages`: finished user and assistant messages, without the greeting, error
 * notes, a slash command's note, or a reply still being written. Opening a chat (which marks the loaded transcript as
 * already saved) and the autosave (which asks whether anything changed) both derive it here: if the two ever differed,
 * merely viewing a synced chat would PUT it back (audit Medium 33).
 */
export function savedTurns(messages) {
    return messages.filter((m) => !m.pending && !m.streaming && !m.welcome && !m.error && !m.note).map((m) => ({ role: m.role, content: m.content }));
}
/**
 * Whether a session opened from the history list may be written to after its fetch, and with which turns.
 *  - loaded: the server's transcript, already saved there (`fromServer`), so viewing it must not PUT it back.
 *  - failed: read-only until a retry loads it; a send or an autosave now would replace the saved conversation.
 *  - missing, for a chat the server listed (`remote`): read-only as well. The server said it holds a conversation by
 *    that id, so "missing" means it was deleted meanwhile, or the server could not read its storage and answered empty
 *    (gyro-svc did that before 2026-10-04). Writing would replace or resurrect it.
 *  - missing, for a local chat never saved: expected, and the local turns stand.
 */
export function decideTranscriptLoad(session, load) {
    if (load.status === 'loaded')
        return { writable: true, turns: load.conversation.turns, fromServer: true };
    if (load.status === 'failed' || session.remote)
        return { writable: false };
    return { writable: true, turns: session.messages, fromServer: false };
}
//# sourceMappingURL=transcriptLoad.js.map